import {getAuth} from 'firebase/auth';
import {range} from 'lodash';

//ts-ignore
import {isProd, useLocalApi} from 'src/lib/env';

export let API_ROOT = 'http://localhost:8080';
//@ts-ignore
if (!useLocalApi || isProd) {
  API_ROOT = 'https://api.upstream.tech';
}

export let SLOW_API_ROOT = 'http://localhost:8080';
//@ts-ignore
if (!useLocalApi || isProd) {
  SLOW_API_ROOT = 'https://slow-api.upstream.tech';
}

type ApiParams = {
  [k: string]: string[] | string | number | number[] | boolean;
};

export type Paging<K> = {
  afterToken: K | null;
  beforeToken: K | null;
  pageIndex?: number;
  isFinishedPaging?: boolean;
};

type ApiResponse<T, K> = {
  data: T;
  paging?: Paging<K>;
  status?: number;
  error?: string;
};

const DEFEAULT_REQUEST_OPTIONS: RequestInit = {
  method: 'GET',
  mode: 'cors',
  cache: 'default',
};

/**
 * contextOrganizationId is to keep track of the organization a user is viewing in Lens.
 * We add this as a header to all API calls to support parent organizations. User's who belong to a parent
 * organization are able to experience Lens as a member of any of their child orgs, but this means the
 * user profile and token are no longer sufficent for knowing what org a request is for, so we need to
 * specify the org. This also avoids the API having to infer the the organization from information like
 * the featureCollection in non parent org cases.
 *
 * setContextOrganizationId is called whenever the organizationId changes to keep the contextOrganizationId global up to date
 */
export let contextOrganizationId: string | null = null;
export const setContextOrganizationId = (id: string | null) => {
  contextOrganizationId = id;
};

// The organization refuses the network this browser is on (api/common/account_policy.py). Any
// request can meet it, since the API checks where each one comes from, and this wrapper hands
// errors back rather than throwing, so it's announced app-wide: AuthProvider turns it into the
// page that explains it.
export const NETWORK_RESTRICTED_HOLD = 'network_restricted';
export const NETWORK_RESTRICTED_EVENT = 'hydroforecast:network-restricted';
export type NetworkRestrictedDetail = {organizationId: string | null};

export async function makeApiRequest<T, K = never>(
  route: string,
  params: null | ApiParams = null,
  options: RequestInit = {},
  {
    noContextOrg = false,
    preserveBodyError = false,
  }: {noContextOrg?: boolean; preserveBodyError?: boolean} = {}
): Promise<ApiResponse<T, K>> {
  const opts = {
    ...DEFEAULT_REQUEST_OPTIONS,
    ...options,
  };
  const pageUrl = new URL(window.location.href);

  const url = getApiRoute(route, params);
  const headers = new Headers();
  const auth = getAuth();

  const apiKey = pageUrl.searchParams.get('apiKey');
  if (apiKey) {
    headers.append('Authorization', apiKey);
  } else if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    if (token) {
      headers.append('Authorization', `jwt ${token}`);
    }
  }

  // POST requests with form data are assigned a Content-Type header based on
  // the type of encoding.
  const isFormPost = opts.method === 'POST' && opts.body instanceof FormData;
  if (!isFormPost) {
    headers.append('Content-Type', 'application/json');
  }

  if (contextOrganizationId && !noContextOrg) {
    headers.append('x-context-organization-id', contextOrganizationId);
  }

  const response = await fetch(url, {
    headers,
    ...opts,
  });
  const responseText = await response.text();
  let data;
  try {
    data = JSON.parse(responseText);
  } catch {
    throw new Error(`HTTP ${response.status}: invalid JSON response`);
  }
  if (response.status === 403 && data?.hold === NETWORK_RESTRICTED_HOLD) {
    // Which org the refused request was for, if it named one: a request under the org the user
    // is in means that org refuses them, where one without means every org does.
    const organizationId = headers.get('x-context-organization-id');
    window.dispatchEvent(
      new CustomEvent<NetworkRestrictedDetail>(NETWORK_RESTRICTED_EVENT, {detail: {organizationId}})
    );
  }
  return {
    ...data,
    status: response.status,
    error: response.status >= 400 ? responseText : preserveBodyError ? data?.error : undefined,
  };
}

function getApiRoute(route: string, params: ApiParams | null = {}): string {
  if (route.startsWith(API_ROOT) || route.startsWith(SLOW_API_ROOT)) {
    return `${route}${serialize(params)}`;
  } else {
    return `${API_ROOT}/${route}${serialize(params)}`;
  }
}

function serialize(
  obj: null | {[key: string]: string[] | string | number[] | number | boolean}
): string {
  if (!obj || !Object.keys(obj).length) {
    return '';
  }

  let str: string[] = [];
  for (const p in obj) {
    const val = obj[p];

    if (Array.isArray(val)) {
      str = str.concat(val.map((v) => `${encodeURIComponent(p)}=${encodeURIComponent(v)}`));
    } else if (Object.hasOwn(obj, p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(val));
    }
  }
  return '?' + str.join('&');
}

export async function getAllPages<T, K>(
  request: (params: ApiParams) => Promise<ApiResponse<T[], K>>,
  params: ApiParams,
  numConcurrentRequests = 8,
  pageIndex = 1
): Promise<ApiResponse<T[], K>> {
  let acc: ApiResponse<T[], K> = {data: []};

  let isDone = false;
  let startPage = pageIndex;
  while (!isDone) {
    const promises = [];
    for (const i of range(0, numConcurrentRequests)) {
      promises.push(request({...params, page: startPage + i}));
    }
    const responses = await Promise.all(promises);
    for (const resp of responses) {
      if (resp.data.length === 0) {
        isDone = true;
        break;
      }

      acc = {
        data: [...acc.data, ...resp.data],
        paging: resp.paging,
      };
    }

    startPage += numConcurrentRequests;
  }

  return acc;
}
